import { useEffect, useState } from 'react';
import { mqtt5, auth, iot } from 'aws-iot-device-sdk-v2';
import { once } from 'events';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { toUtf8 } from '@aws-sdk/util-utf8-browser';
import {
  AWS_REGION,
  AWS_COGNITO_IDENTITY_POOL_ID,
  AWS_IOT_ENDPOINT,
} from './iot-settings.js';

function log(msg) {
  let now = new Date();
  console.log(`${now.toString()}: ${msg}`);
}

class AWSCognitoCredentialsProvider extends auth.CredentialsProvider {
  constructor(options, expire_interval_in_ms) {
    super();
    this.options = options;

    setInterval(async () => {
      await this.refreshCredentials();
    }, expire_interval_in_ms ?? 3600 * 1000);
  }

  getCredentials() {
    return {
      aws_access_id: this.cachedCredentials?.accessKeyId ?? '',
      aws_secret_key: this.cachedCredentials?.secretAccessKey ?? '',
      aws_sts_token: this.cachedCredentials?.sessionToken,
      aws_region: this.options.Region,
    };
  }

  async refreshCredentials() {
    log('Fetching Cognito credentials');
    this.cachedCredentials = await fromCognitoIdentityPool({
      identityPoolId: this.options.IdentityPoolId,
      clientConfig: { region: this.options.Region },
    })();
  }
}

function Mqtt5({ liftTelemetryPayload, devicename }) {
  const [client, setClient] = useState(null);

  const topicName = `telemetry/${devicename}`;

  function createClient(provider) {
    let wsConfig = {
      credentialsProvider: provider,
      region: AWS_REGION,
    };

    let builder =
      iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(
        AWS_IOT_ENDPOINT,
        wsConfig
      );

    let client = new mqtt5.Mqtt5Client(builder.build());

    client.on('error', (error) => {
      log('Error event: ' + error.toString());
    });

    client.on('messageReceived', (eventData) => {
      if (eventData.message.payload) {
        liftTelemetryPayload(
          JSON.parse(toUtf8(eventData.message.payload)),
          devicename
        );
      }
    });

    client.on('attemptingConnect', () => {
      log('Attempting Connect event');
    });

    client.on('connectionSuccess', (eventData) => {
      log('Connection Success event');
      log('Connack: ' + JSON.stringify(eventData.connack));
      log('Settings: ' + JSON.stringify(eventData.settings));
    });

    client.on('connectionFailure', (eventData) => {
      log('Connection failure event: ' + eventData.error.toString());
    });

    client.on('disconnection', (eventData) => {
      log('Disconnection event: ' + eventData.error.toString());
      if (eventData.disconnect !== undefined) {
        log('Disconnect packet: ' + JSON.stringify(eventData.disconnect));
      }
    });

    client.on('stopped', () => {
      log('Stopped event');
    });

    return client;
  }

  async function testSuccessfulConnection() {
    const provider = new AWSCognitoCredentialsProvider({
      IdentityPoolId: AWS_COGNITO_IDENTITY_POOL_ID,
      Region: AWS_REGION,
    });
    await provider.refreshCredentials();
    const mqttClient = createClient(provider);
    setClient(mqttClient);
    const attemptingConnect = once(mqttClient, 'attemptingConnect');
    const connectionSuccess = once(mqttClient, 'connectionSuccess');
    mqttClient.start();
    await attemptingConnect;
    await connectionSuccess;
    const suback = await mqttClient.subscribe({
      subscriptions: [{ qos: mqtt5.QoS.AtMostOnce, topicFilter: topicName }],
    });
    log('Suback result: ' + JSON.stringify(suback));
    // let unsuback = await mqttClient.unsubscribe({
    //   topicFilters: [topicName],
    // });
    // log('Unsuback result: ' + JSON.stringify(unsuback));
  }

  useEffect(() => {
    testSuccessfulConnection(); // initial execution

    return () => {
      if (client) {
        const disconnection = once(client, 'disconnection');
        const stopped = once(client, 'stopped');
        client.stop();
        Promise.all([disconnection, stopped]).then(() => {
          setClient(null);
        });
      }
    };
  }, []);
}

export default Mqtt5;
